import React, { useState, useEffect } from 'react';
import { warehouseService } from '../../services/warehouseService';
import { useLocation } from '../../contexts/LocationContext';
import {
  AlertBanner,
  TabSwitcher,
  ProductSelectionModal,
  HistoryPanel,
  SelectedProductsTable,
  DetailsModal,
  StatusBadge,
  useWarehouses,
  useInventoryProducts,
  useProductSearch,
  useSelectedProducts,
} from './WarehouseDocumentBase';

const InternalIssue = () => {
  const { locationId } = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showProductModal, setShowProductModal] = useState(false);
  const [documentDate, setDocumentDate] = useState(new Date().toISOString().split('T')[0]);

  // Magazyny
  const { warehouses, selectedWarehouse, setSelectedWarehouse } = useWarehouses(locationId);

  // Produkty z magazynu
  const { products, loadingProducts, loadProducts } = useInventoryProducts(selectedWarehouse);

  // Wyszukiwanie z debounce
  useProductSearch(showProductModal, searchTerm, loadProducts, [selectedWarehouse]);

  // Wybrane produkty
  const {
    selectedProducts,
    addProduct: baseAddProduct,
    updateQuantity: updateProductQuantity,
    updateReason: updateProductReason,
    removeProduct,
    clearProducts
  } = useSelectedProducts(setError);

  // Historia RW
  const [issues, setIssues] = useState([]);
  const [activeTab, setActiveTab] = useState('new');
  const [dateFilter, setDateFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  // Podglad szczegolow RW
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  useEffect(() => {
    if (activeTab === 'history' && locationId) {
      loadIssues();
    }
  }, [activeTab, locationId]);

  const loadIssues = async () => {
    try {
      const result = await warehouseService.getInternalIssues({
        date: dateFilter,
        status: statusFilter,
        location_id: locationId
      });
      if (result.success) {
        setIssues(result.data || []);
      }
    } catch (err) {
      console.error('Błąd ładowania historii RW:', err);
    }
  };

  const loadPreview = async (issueId) => {
    setLoadingPreview(true);
    try {
      const result = await warehouseService.getInternalIssueDetails(issueId);
      if (result.success) {
        setPreviewData(result.data);
        setShowPreviewModal(true);
      } else {
        setError('Błąd ładowania szczegółów dokumentu');
      }
    } catch (err) {
      console.error('Błąd ładowania szczegółów RW:', err);
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoadingPreview(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'history') {
      loadIssues();
    }
  }, [dateFilter, statusFilter]);

  const addProduct = (product) => {
    baseAddProduct(product);
    setShowProductModal(false);
    setSearchTerm('');
  };

  const submitInternalIssue = async () => {
    if (selectedProducts.length === 0) {
      setError('Dodaj przynajmniej jeden produkt');
      return;
    }

    const missingReason = selectedProducts.find(p => !p.reason.trim());
    if (missingReason) {
      setError('Wszystkie produkty muszą mieć podany powód rozchodu');
      return;
    }

    setLoading(true);
    try {
      const result = await warehouseService.createInternalIssue({
        products: selectedProducts.map(p => ({
          product_id: p.id,
          quantity: p.quantity,
          reason: p.reason
        })),
        location_id: locationId,
        document_date: documentDate
      });

      if (result.success) {
        setSuccess('Rozchód wewnętrzny został zarejestrowany pomyślnie');
        clearProducts();
        loadIssues();
        setTimeout(() => setSuccess(''), 3000);
        loadProducts();
      } else {
        setError(result.error || 'Błąd podczas rejestrowania rozchodu');
      }
    } catch (err) {
      setError('Błąd połączenia z serwerem');
    } finally {
      setLoading(false);
    }
  };

  const historyColumns = [
    { label: 'Data' },
    { label: 'Nr dokumentu' },
    { label: 'Pozycje' },
    { label: 'Status' },
    { label: 'Utworzył' },
    { label: 'Akcje', align: 'center' },
  ];

  const renderHistoryRow = (issue) => (
    <tr key={issue.id}>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
        {new Date(issue.created_at).toLocaleDateString('pl-PL')}
        <span style={{ display: 'block', color: 'var(--text-muted)', fontSize: '10px' }}>
          {new Date(issue.created_at).toLocaleTimeString('pl-PL')}
        </span>
      </td>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
        <code style={{ fontSize: '10px' }}>{issue.document_number}</code>
      </td>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
        <span style={{
          padding: '0.1rem 0.3rem',
          fontSize: '10px',
          backgroundColor: 'var(--warning)',
          color: '#000',
          borderRadius: '3px'
        }}>
          {issue.items_count || 0} poz.
        </span>
      </td>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)' }}>
        <StatusBadge status={issue.status} />
      </td>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', color: 'var(--text-muted)', fontSize: '10px' }}>
        {issue.created_by || 'System'}
      </td>
      <td style={{ padding: '0.3rem 0.5rem', borderBottom: '1px solid var(--border)', textAlign: 'center' }}>
        <button
          onClick={() => loadPreview(issue.id)}
          disabled={loadingPreview}
          style={{
            padding: '4px 10px',
            fontSize: '11px',
            border: '1px solid var(--info)', color: 'var(--info)' ,
            cursor: 'pointer'
          }}
          title="Podgląd"
        >
          <i className="fas fa-eye"></i>
        </button>
      </td>
    </tr>
  );

  return (
    <div style={{ fontSize: '12px' }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '0.5rem'
      }}>
        <span style={{ fontWeight: '600', fontSize: '13px' }}>📤 Rozch{'ó'}d Wewn{'ę'}trzny (RW)</span>
      </div>

      <AlertBanner type="error" message={error} onClose={() => setError('')} />
      <AlertBanner type="success" message={success} onClose={() => setSuccess('')} />

      <TabSwitcher
        activeTab={activeTab}
        onTabChange={setActiveTab}
        tabs={[
          { key: 'new', label: 'Nowy rozchód', icon: 'fas fa-plus', activeColor: 'var(--orange)' },
          { key: 'history', label: 'Historia rozchodów', icon: 'fas fa-history', activeColor: 'var(--purple)' },
        ]}
      />

      {activeTab === 'new' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
            <button
              style={{
                padding: '6px 12px',
                fontSize: '12px',
                border: '1px solid var(--info)', color: 'var(--info)' ,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem'
              }}
              onClick={() => setShowProductModal(true)}
            >
              <i className="fas fa-plus" style={{ fontSize: '10px' }}></i>
              Dodaj produkt
            </button>
          </div>

          <SelectedProductsTable
            products={selectedProducts}
            onQuantityChange={updateProductQuantity}
            onReasonChange={updateProductReason}
            onRemove={removeProduct}
            onSubmit={submitInternalIssue}
            loading={loading}
            tableTitle="Produkty do wydania"
            submitLabel="Zatwierdź rozchód"
            submitIcon="fas fa-arrow-up"
            submitColor="var(--warning)"
            showStock={true}
            showReason={true}
            reasonPlaceholder="Powód rozchodu..."
            reasonRequired={true}
            documentDate={documentDate}
            onDocumentDateChange={setDocumentDate}
          />
        </>
      )}

      {activeTab === 'history' && (
        <HistoryPanel
          items={issues}
          columns={historyColumns}
          dateFilter={dateFilter}
          onDateFilterChange={setDateFilter}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          onRefresh={loadIssues}
          emptyMessage="Brak rozchodów wewnętrznych"
          title="Historia rozchodów wewnętrznych"
          renderRow={renderHistoryRow}
        />
      )}

      {/* Product selection modal */}
      <ProductSelectionModal
        show={showProductModal}
        onClose={() => { setShowProductModal(false); setSearchTerm(''); }}
        products={products}
        loadingProducts={loadingProducts}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        selectedProducts={selectedProducts}
        onAddProduct={addProduct}
        warehouses={warehouses}
        selectedWarehouse={selectedWarehouse}
        onWarehouseChange={setSelectedWarehouse}
        gradient="linear-gradient(135deg, var(--orange), var(--danger))"
        spinnerColor="var(--orange)"
        addButtonColor="var(--orange)"
        addButtonHoverColor="var(--danger)"
        icon={'📤'}
        title="Wybierz produkt do wydania"
        subtitle="RW - Rozchód wewnętrzny"
        warehouseLabel={'🏪 Magazyn źródłowy'}
        hintBg="var(--warning-light)"
        hintColor="var(--warning)"
        hintIcon={'⚠️'}
        hintText="Rozchód wewnętrzny zmniejsza stan magazynowy. Upewnij się, że podajesz prawidłową ilość i powód."
      />

      {/* Preview modal */}
      <DetailsModal
        show={showPreviewModal && !!previewData}
        onClose={() => { setShowPreviewModal(false); setPreviewData(null); }}
        title={`RW: ${previewData?.document_number || ''}`}
        headerBg="var(--danger)"
      >
        {previewData && (
          <>
            {previewData.notes && (
              <div style={{ marginBottom: '0.75rem', padding: '0.5rem', backgroundColor: 'var(--bg)', borderRadius: '4px', fontSize: '11px' }}>
                <strong>Uwagi:</strong> {previewData.notes}
              </div>
            )}
            <table style={{ width: '100%', fontSize: '11px', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: 'var(--bg)' }}>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>Produkt</th>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>Kod</th>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>Ilo{'ść'}</th>
                  <th style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'left' }}>Pow{'ó'}d</th>
                </tr>
              </thead>
              <tbody>
                {(previewData.items || []).map((item, idx) => (
                  <tr key={idx}>
                    <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)' }}>{item.product_name}</td>
                    <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)' }}>
                      <code style={{ fontSize: '10px' }}>{item.product_code}</code>
                    </td>
                    <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', textAlign: 'right' }}>
                      <strong>{item.quantity}</strong>
                    </td>
                    <td style={{ padding: '0.4rem', borderBottom: '1px solid var(--border)', fontSize: '10px', color: 'var(--text-muted)' }}>
                      {item.reason || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {(!previewData.items || previewData.items.length === 0) && (
              <div style={{ textAlign: 'center', padding: '1rem', color: 'var(--text-muted)' }}>
                Brak pozycji
              </div>
            )}
          </>
        )}
      </DetailsModal>
    </div>
  );
};

export default InternalIssue;
